import { Button, useToast } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useCallbackRef } from "../../shared/hooks/useCallbackRef";
import useApi from "../../shared/hooks/useApi";
import { PatientDocumentScheduleId } from "../../shared/schema/schema";

type Props = {
  documentId: PatientDocumentScheduleId;
  htmlTemplateId: string;
  readonly?: boolean;
  /** After the section's codes were resolved. */
  onChanged?: () => void;
  /** Whether resolving is in flight. */
  onSavingChange?: (isSaving: boolean) => void;
};

export default function PatientDocumentSectionedIcdCodes({
  documentId,
  htmlTemplateId,
  readonly = false,
  onChanged,
  onSavingChange,
}: Props) {
  const { mutations } = useApi();
  const toast = useToast();

  const saveIcdCodesMutation = useMutation({
    ...mutations.patientDocument.saveIcdCodes(documentId, htmlTemplateId),
    onSuccess: () => {
      toast({
        title: "Diagnosis codes resolved",
        status: "success",
        position: "top-right",
      });
      onChanged?.();
    },
    onError: (error) => {
      toast({
        title: "Error resolving diagnosis codes",
        description: (error as Error).message,
        status: "error",
        position: "top-right",
      });
    },
  });

  const reportSaving = useCallbackRef(onSavingChange);
  const isSaving = saveIcdCodesMutation.isPending;
  useEffect(() => {
    reportSaving(isSaving);
  }, [isSaving, reportSaving]);

  if (readonly) {
    return null;
  }

  return (
    <Button
      colorScheme="blue"
      isLoading={isSaving}
      mt={2}
      size="sm"
      onClick={() => saveIcdCodesMutation.mutate()}
    >
      Resolve diagnosis codes for this section
    </Button>
  );
}
